import { useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import {
  normalizeUserPermissions,
  userHasPermission,
  canManageClientRecords,
  PERMISSIONS,
} from 'constants/permissions';
import { useAccountingView } from 'hooks/useAccountingView';

export function usePermissions() {
  const { user } = useAuth();
  const { isTaxView } = useAccountingView();

  const permissions = useMemo(() => normalizeUserPermissions(user), [user]);

  const can = (key) => userHasPermission(user, key);

  const financeBlocked = isTaxView;

  const isDoctor = !!(user?.role === 'doctor' || user?.role === 'surgeon' || user?.role === 'anesthesiologist' || user?.isDoctor);
  const isNurse = !!(user?.role === 'nurse' || user?.isNurse);
  const isImagingRole = !!(user?.role === 'imaging' || user?.isImaging);
  const actsAsDoctor = !!(
    user?.actsAsDoctor ||
    isDoctor ||
    ((user?.isDirector || user?.role === 'director' || user?.isAdmin || user?.role === 'admin') &&
      user?.doctorId)
  );

  return {
    user,
    permissions,
    can,
    isDirector: !!(user?.isDirector || user?.role === 'director'),
    isDoctor,
    isNurse,
    actsAsDoctor,
    canManageUsers: can(PERMISSIONS.SETTINGS_USERS),
    canManageAi: can(PERMISSIONS.SETTINGS_AI),
    canManageClients: canManageClientRecords(user),
    canManageBookings: (!isDoctor || isImagingRole) && can(PERMISSIONS.SCHEDULE),
    canViewExpenses: !isDoctor && can(PERMISSIONS.EXPENSES) && !financeBlocked,
    canViewPayroll: can(PERMISSIONS.PAYROLL) && !financeBlocked,
    canManagePayroll: can(PERMISSIONS.PAYROLL_MANAGE) && !isDoctor && !financeBlocked,
    canViewOwnPayroll: isDoctor && can(PERMISSIONS.PAYROLL),
    canViewReceptionLeads: can(PERMISSIONS.RECEPTION_LEADS),
    canViewFinance: !isDoctor && can(PERMISSIONS.DASHBOARD_FINANCE) && !financeBlocked,
    canViewAttendance: can(PERMISSIONS.ATTENDANCE) && !financeBlocked,
    canManageAttendance: can(PERMISSIONS.ATTENDANCE_MANAGE) && !financeBlocked,
    canReceptionShifts: can(PERMISSIONS.RECEPTION_SHIFTS) && !financeBlocked,
    canManageReceptionShifts: can(PERMISSIONS.RECEPTION_SHIFTS_MANAGE) && !financeBlocked,
    canDoctorShifts: can(PERMISSIONS.DOCTOR_SHIFTS) && !financeBlocked,
    canManageDoctorShifts: can(PERMISSIONS.DOCTOR_SHIFTS_MANAGE) && !financeBlocked,
    canViewPharmacy: can(PERMISSIONS.PHARMACY) && !financeBlocked,
    isImagingSpecialist:
      can(PERMISSIONS.IMAGING_STUDIES) && !can(PERMISSIONS.MEDICAL_RECORDS) && !user?.isDirector,
    isImagingOnly:
      isImagingRole ||
      (can(PERMISSIONS.IMAGING_STUDIES) &&
        !can(PERMISSIONS.MEDICAL_RECORDS) &&
        !user?.isDirector),
    isImagingRole,
  };
}

export default usePermissions;
